import Localized from 'src/constants/AppStrings';
import {ORDER_TRACKER_CONFIG} from 'src/config/orderTracker';

export type EtaParts = {
  kind: 'at' | 'within' | 'raw';
  prefix: string;
  value: string;
  fullText: string;
};

/** Safely read a label string. */
function getLabel(key: string): string | undefined {
  const obj = (Localized as unknown as {Labels?: Record<string, unknown>})
    ?.Labels;
  const v = obj?.[key];
  return typeof v === 'string' ? v : undefined;
}

function partsFromTemplate(
  template: string,
  placeholder: '{time}' | '{minutes}',
  injected: string,
): EtaParts {
  if (template.includes(placeholder)) {
    const [before, after = ''] = template.split(placeholder);
    const prefix = before ?? '';
    const value = `${injected}${after}`;
    return {
      kind: placeholder === '{time}' ? 'at' : 'within',
      prefix,
      value,
      fullText: `${prefix}${value}`,
    };
  }

  return {kind: 'raw', prefix: '', value: '', fullText: template};
}

/** Try to parse a "HH:mm[:ss]" clock string. Returns {h,m,s} or null. */
function parseClockString(
  s: string,
): {h: number; m: number; s?: number} | null {
  const m = /^\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*$/.exec(s);
  if (!m) {
    return null;
  }
  const h = Number(m[1]);
  const min = Number(m[2]);
  const sec = m[3] ? Number(m[3]) : undefined;
  if (Number.isNaN(h) || Number.isNaN(min)) {
    return null;
  }
  if (h < 0 || h > 23 || min < 0 || min > 59) {
    return null;
  }
  if (sec != null && (sec < 0 || sec > 59)) {
    return null;
  }
  return {h, m: min, s: sec};
}

/** Format a device-locale time for a given ISO datetime + optional TZ. */
function formatIsoTime(iso: string, tz?: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: tz || undefined,
    }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
}

function formatFromClock(clock: string, tz?: string): string {
  const parsed = parseClockString(clock);
  if (!parsed) {
    return clock.trim();
  }
  const now = new Date();
  const d = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    parsed.h,
    parsed.m,
    parsed.s ?? 0,
    0,
  );
  return formatIsoTime(d.toISOString(), tz);
}

export const buildEtaParts = (
  orderType: 'OrderNow' | 'OrderAhead',
  etaText?: string,
  estimatedPickupTime?: string,
  pickupTimeZone?: string,
): EtaParts => {
  if (orderType === 'OrderNow') {
    const minutes = String(ORDER_TRACKER_CONFIG.READY_HIDE_TTL_MIN ?? 15);
    const withinTpl = getLabel('ready_for_pickup_within_x_minutes');
    if (withinTpl) {
      return partsFromTemplate(withinTpl, '{minutes}', minutes);
    }
    const fallback = getLabel('ready_for_pickup_within_15_minutes');
    if (fallback) {
      return {kind: 'within', prefix: '', value: fallback, fullText: fallback};
    }

    return {kind: 'within', prefix: '', value: minutes, fullText: minutes};
  }

  if (etaText && etaText.trim().length > 0) {
    const trimmed = etaText.trim();

    const clock = parseClockString(trimmed);
    if (clock) {
      const timeStr = formatFromClock(trimmed, pickupTimeZone);

      const tplOA = getLabel('order_is_ready_for_pickup_at_time');
      const tpl = tplOA || getLabel('ready_for_pickup_at_time');
      if (tpl) {
        return partsFromTemplate(tpl, '{time}', timeStr);
      }

      return {kind: 'at', prefix: '', value: timeStr, fullText: timeStr};
    }

    return {kind: 'raw', prefix: trimmed, value: '', fullText: trimmed};
  }

  if (orderType === 'OrderAhead' && estimatedPickupTime) {
    const timeStr = formatIsoTime(estimatedPickupTime, pickupTimeZone);
    const tplOA = getLabel('order_is_ready_for_pickup_at_time');
    const tpl = tplOA || getLabel('ready_for_pickup_at_time');
    if (tpl) {
      return partsFromTemplate(tpl, '{time}', timeStr);
    }

    const generic = getLabel('ready_to_pick_up');
    if (generic) {
      return {kind: 'raw', prefix: generic, value: '', fullText: generic};
    }
    return {kind: 'at', prefix: '', value: timeStr, fullText: timeStr};
  }

  const minutes = String(ORDER_TRACKER_CONFIG.READY_HIDE_TTL_MIN ?? 15);
  const withinTpl = getLabel('ready_for_pickup_within_x_minutes');
  if (withinTpl) {
    return partsFromTemplate(withinTpl, '{minutes}', minutes);
  }
  const fallback = getLabel('ready_for_pickup_within_15_minutes');
  if (fallback) {
    return {kind: 'within', prefix: '', value: fallback, fullText: fallback};
  }
  return {kind: 'within', prefix: '', value: minutes, fullText: minutes};
};

export const buildEtaText = (
  orderType: 'OrderNow' | 'OrderAhead',
  etaText?: string,
  estimatedPickupTime?: string,
  pickupTimeZone?: string,
): string => {
  return buildEtaParts(orderType, etaText, estimatedPickupTime, pickupTimeZone)
    .fullText;
};
